<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { DateTime } from 'luxon';
import { LuxonDateTimeFormats, t, toKebabCase } from '@nova/core';

import { isMobile } from '@satellite/plugins/util';
import { useCustomFieldProps } from '@satellite/composables/useCustomFieldProps';
import DatePicker from '@satellite/components/elements/DatePicker.vue';
import TimeEntry from '@satellite/components/elements/TimeEntry.vue';

import CustomFieldInputBase from '../CustomFieldInputBase.vue';

import type { CustomFieldProps, TimestampValue } from '../types';

/**
 * Timestamp field type - date and time input with timezone support.
 *
 * Narrows modelValue to what this field type round-trips. The shared CustomFieldProps.modelValue
 * covers the scalar shapes the other 13 field types use and does not include DateTime, so a
 * parent feeding back the value this component emits would otherwise fail to type-check.
 */
const {
  hideDetails = 'auto',
  displayLabel = true,
  required = undefined,
  compact = isMobile(),
  ...props
} = defineProps<Omit<CustomFieldProps, 'modelValue'> & { modelValue?: string | DateTime | null }>();

const emit = defineEmits<{
  'update:model-value': [value: TimestampValue];
  /** @deprecated Use update:model-value instead */
  input: [value: TimestampValue];
}>();

const { normalizedProps } = useCustomFieldProps(() => ({
  ...props,
  hideDetails,
  displayLabel,
  required
}));

const customDate = ref('');
const customTime = ref({ amPm: '', time: '' });

const militaryTimeEnabled = computed(() => props.militaryTimeEnabled ?? false);

/**
 * Combines customDate + customTime into a single Luxon DateTime in the field's timezone. Stays a
 * DateTime in here — it is what `isSameInstant` compares against the incoming value — and is
 * serialized to ISO only at the emit.
 */
const dateTime = computed((): DateTime | null => {
  const dateStr = DateTime.fromISO(customDate.value).toFormat('yyyy-MM-dd');
  /**
   * 'h:mm' / 'H:mm' (not 'hh:mm' / 'HH:mm') so a single-digit hour parses: TimeEntry pads only
   * on blur but emits on every keystroke, so this sees "6:49" while the user is still typing.
   * The unpadded tokens accept both "6:49" and "06:49"; the padded ones reject the former.
   */
  const timeFormat = militaryTimeEnabled.value ? 'H:mm' : 'h:mm a';
  const timeStr = militaryTimeEnabled.value
    ? customTime.value.time
    : [customTime.value.time, customTime.value.amPm].join(' ').trim();
  const formatStr = `${LuxonDateTimeFormats.DateDashed} ${timeFormat}`;
  const newDateTime = DateTime.fromFormat(`${dateStr} ${timeStr}`, formatStr).setZone(
    props.timezone,
    { keepLocalTime: true }
  );
  return newDateTime?.isValid ? newDateTime : null;
});

/** The value the parent handed over, parsed once so a sync can be told apart from a user edit. */
const modelValueDateTime = computed((): DateTime | null => {
  if (!props.modelValue) {
    return null;
  }
  /**
   * Handled explicitly rather than through String(): a DateTime only survived that because its
   * toString() happens to produce ISO text, which is coincidence rather than contract.
   */
  const parsed = DateTime.isDateTime(props.modelValue)
    ? props.modelValue
    : DateTime.fromISO(String(props.modelValue));
  return parsed.isValid ? parsed.setZone(props.timezone) : null;
});

function isSameInstant(a: DateTime | null, b: DateTime | null): boolean {
  if (!a || !b) {
    return !a && !b;
  }
  return a.toMillis() === b.toMillis();
}

/**
 * Emits null whenever the controls stop holding a complete instant, required or not. Holding the
 * emit back for a required field looked safer, but it meant clearing the date left the parent on
 * the previous value: the input went blank while the old timestamp stayed in the form, and in
 * apps without the $validator global (Circe, Juno) nothing flagged the empty field, so the stale
 * value submitted. Flagging a missing value is the required rule's job, not this emit's.
 */
function emitValueUpdate() {
  /**
   * Seeding the controls from modelValue moves customDate/customTime, which trips the same
   * watchers a user edit does. Comparing the derived instant to the incoming one keeps that
   * sync from echoing the parent's own value straight back as a change.
   */
  if (!props.returnObject && isSameInstant(dateTime.value, modelValueDateTime.value)) {
    return;
  }
  const payload: TimestampValue = props.returnObject
    ? { date: customDate.value, time: customTime.value.time, amPm: customTime.value.amPm }
    : (dateTime.value?.toISO() ?? null);
  emit('update:model-value', payload);
  emit('input', payload);
}

/**
 * Watched rather than run once on mount: a parent that loads the timestamp asynchronously or
 * resets the form leaves the controls showing the previous value otherwise, and a later edit
 * then emits that stale state back.
 */
function syncFromModelValue() {
  if (!props.modelValue) {
    /**
     * A parent reset clears the controls, but only when they hold a complete instant. An emptied
     * modelValue is also this component's own null echoing back while the user is mid-entry
     * (date picked, time half typed), and clearing then would wipe the date they just chose.
     */
    if (dateTime.value) {
      customDate.value = '';
      customTime.value = { amPm: '', time: '' };
    }
    return;
  }
  const currentDate = modelValueDateTime.value;
  if (!currentDate) {
    return;
  }
  customDate.value = currentDate.toFormat(LuxonDateTimeFormats.DateDashed);
  if (militaryTimeEnabled.value) {
    customTime.value = { ...customTime.value, time: currentDate.toFormat('HH:mm') };
  } else {
    customTime.value = {
      time: currentDate.toFormat(LuxonDateTimeFormats.Extended12HrTimePadding),
      amPm: currentDate.toFormat(LuxonDateTimeFormats.AMPM).toLowerCase()
    };
  }
}

watch(() => props.modelValue, syncFromModelValue, { immediate: true });

watch(customDate, () => emitValueUpdate());
watch(customTime, () => emitValueUpdate());
</script>

<template>
  <custom-field-input-base
    :field="field"
    v-bind="normalizedProps"
    :data-testid="testId || toKebabCase(field.label)">
    <div class="d-flex flex-row flex-wrap ga-4 align-start">
      <date-picker
        compact-display
        no-margins
        outlined
        class="flex-grow-1"
        v-model="customDate"
        :display-field-icon="false"
        :disabled="normalizedProps.readOnly"
        :display-field-label="normalizedProps.displayLabel"
        :placeholder="t('customFields.select_date')"
        :required="normalizedProps.required"
        :test-id="`${testId || toKebabCase(field.label)}-date-picker`" />

      <time-entry
        class="flex-grow-1"
        :compact="compact"
        :disabled="normalizedProps.readOnly"
        :hide-timezone="hideTimezone"
        :military-time-enabled="militaryTimeEnabled"
        :required="normalizedProps.required"
        :test-id="`${testId || toKebabCase(field.label)}-time-input`"
        :timezone="timezone"
        v-model="customTime" />
    </div>
  </custom-field-input-base>
</template>
